import { useCallback, useEffect, useState } from 'react';
import { leadgenApi, type LeadGenConsentRecord } from '@/api/leadgen.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Textarea } from '@/components/ui/Textarea';
import { showApiError } from '@/utils/apiError';

export function LeadGenConsentPage() {
  const [consents, setConsents] = useState<LeadGenConsentRecord[]>([]);

  const load = useCallback(async () => {
    try {
      setConsents(await leadgenApi.listConsentRecords());
    } catch (err) {
      showApiError(err, 'Failed to load consent logs');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="page-content">
      <PageHeader
        title="GDPR & Consent Tracking"
        description="Configure privacy banners, track policy consent versions, and audit user acceptance logs."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Lead Gen', to: '/lead-gen' },
          { label: 'Consent' },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card p-5 space-y-4">
          <h3 className="section-title">GDPR Consent Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
              <Textarea
                label="Banner Text (Cookie Consent)"
                className="text-xs h-24"
                defaultValue="We use cookies to analyze web traffic and optimize your marketing experience. By clicking Accept, you consent to our cookies policy."
              />
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
              <Textarea
                label="Privacy Statement (Forms)"
                className="text-xs h-24"
                defaultValue="By submitting this form, you authorize our sales teams to contact you in accordance with our GDPR Privacy Policy (v1.4)."
              />
            </div>
          </div>
          
          <div className="border border-slate-100 rounded-lg p-4 bg-slate-50 space-y-2 text-xs text-slate-500">
            <h4 className="font-semibold text-slate-700">Consent Compliance Versioning</h4>
            <p>Active Compliance Policy: <span className="font-mono text-primary-600 bg-white px-2 py-0.5 rounded border border-slate-200 dark:bg-slate-800 dark:text-primary-300 dark:border-slate-700">GDPR-V1.4</span></p>
            <p>Last Modified: May 30, 2026</p>
          </div>
        </div>

        <div className="card p-5 space-y-4">
          <h3 className="section-title">Acceptance Audit Log</h3>
          <ul className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
            {consents.map(c => (
              <li key={c.id} className="text-xs p-3 rounded-lg border border-slate-100 bg-slate-50 space-y-2">
                <div className="flex justify-between items-start">
                  <span className="font-semibold text-slate-700">{c.email}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">{c.consentVersion}</span>
                </div>
                <div className="flex justify-between text-[10px] text-slate-400">
                  <span>Cookies: {c.cookieConsent ? 'Accepted' : 'Declined'}</span>
                  <span>Privacy: {c.privacyAcceptance ? 'Accepted' : 'Declined'}</span>
                </div>
              </li>
            ))}
            {consents.length === 0 && (
              <li className="text-center p-4 text-slate-400 text-sm">No consent records audit logs.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
